import { Alert, Button, Dropdown, Empty, Skeleton, Typography } from 'antd';
import { DownOutlined, FileTextOutlined, GlobalOutlined, PlusOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useSources } from '@entities/source';
import { extractErrorMessage } from '@shared/api';
import { SourceCard } from './SourceCard';

const { Text } = Typography;

interface Props {
  assistantId: string;
  selectedSourceId: string | null;
  onSelectSource: (sourceId: string) => void;
  onAddDocument: () => void;
  onAddSite: () => void;
}

/**
 * Левая панель «База знаний»: список источников + кнопка добавить.
 *
 * Кнопка «+ Добавить» — дропдаун с двумя опциями (Документ / Сайт).
 * Клик не открывает модалку, а переключает правую панель в режим
 * 'add-document' или 'add-site'. Это решение из обсуждения с владельцем
 * — модалки заменяем на встроенные формы в правой панели.
 *
 * Polling статусов происходит на уровне отдельных SourceCard
 * (для активных источников). Список перезапрашивается через React Query
 * по invalidate из других мест (upload, delete).
 */
export function SourcesList({
  assistantId,
  selectedSourceId,
  onSelectSource,
  onAddDocument,
  onAddSite,
}: Props) {
  const { data: sources, isLoading, error } = useSources(assistantId);

  const menuItems: MenuProps['items'] = [
    {
      key: 'document',
      icon: <FileTextOutlined />,
      label: 'Документ',
      onClick: onAddDocument,
    },
    {
      key: 'site',
      icon: <GlobalOutlined />,
      label: 'Сайт',
      onClick: onAddSite,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: 12 }}>
        <Dropdown menu={{ items: menuItems }} trigger={['click']}>
          <Button type="primary" icon={<PlusOutlined />} block>
            Добавить источник <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {isLoading && <Skeleton active paragraph={{ rows: 2 }} />}

        {error && (
          <Alert
            type="error"
            showIcon
            message="Не удалось загрузить список"
            description={extractErrorMessage(error)}
          />
        )}

        {sources && sources.length === 0 && !isLoading && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Пока пусто
              </Text>
            }
            style={{ marginTop: 24 }}
          />
        )}

        {sources && sources.length > 0 && (
          <div>
            {sources.map((s) => (
              <SourceCard
                key={s.source_id}
                source={s}
                isSelected={s.source_id === selectedSourceId}
                onSelect={() => onSelectSource(s.source_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
